import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, User, Link2, QrCode, BarChart3, Settings, LogOut, Menu, Sun, Moon, CreditCard, MapPin, CircleHelp,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import { useLanguage } from "@/contexts/LanguageContext";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, labelKey: "nav.overview", testId: "overview", end: true },
  { to: "/dashboard/profile", icon: User, labelKey: "nav.profile", testId: "profile" },
  { to: "/dashboard/links", icon: Link2, labelKey: "nav.links", testId: "links" },
  { to: "/dashboard/qrcode", icon: QrCode, labelKey: "nav.qr", testId: "qr code" },
  { to: "/dashboard/attendance", icon: MapPin, labelKey: "nav.attendance", testId: "smart attendance" },
  { to: "/dashboard/analytics", icon: BarChart3, labelKey: "nav.analytics", testId: "analytics" },
  { to: "/dashboard/subscription", icon: CreditCard, labelKey: "nav.subscription", testId: "subscription" },
  { to: "/dashboard/settings", icon: Settings, labelKey: "nav.settings", testId: "settings" },
  { to: "/dashboard/help", icon: CircleHelp, labelKey: "nav.help", testId: "help" },
];

function SidebarNav({ onItemClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <QrCode className="text-primary" size={22} />
          <span className="font-heading text-lg font-bold tracking-tight">Smart-QR</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-primary/10 text-primary nav-active"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
            data-testid={`nav-${item.testId}`}
          >
            <item.icon size={18} strokeWidth={1.8} />
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center justify-between px-3 mb-2">
          <p className="py-1 text-xs text-muted-foreground truncate">{user?.email}</p>
          <button onClick={toggleTheme} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-testid="theme-toggle">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors w-full"
          data-testid="nav-logout"
        >
          <LogOut size={18} strokeWidth={1.8} />
          {t("nav.logout")}
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const { user, sessionKey } = useAuth();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <aside className="hidden md:flex w-60 border-r border-border flex-col shrink-0 bg-sidebar">
        <SidebarNav />
      </aside>

      <div className="md:hidden fixed top-4 left-4 z-50">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn">
              <Menu size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 bg-sidebar">
            <SidebarNav onItemClick={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <main className="flex-1 overflow-auto">
        <EmailVerificationBanner />
        <div className="p-6 md:p-8 pt-16 md:pt-8 max-w-6xl">
          {/* Remount all dashboard pages on account switch to prevent cache leakage */}
          <Outlet key={`dash-${user?.id || "anon"}-${sessionKey}`} />
        </div>
      </main>
    </div>
  );
}
